import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Button, IconButton, Tooltip } from '@mui/material';
import classNames from 'classnames';
import { Field, FieldProps, Form, Formik } from 'formik';
import { useCallback } from 'react';
import * as yup from 'yup';
import { NumericInput } from '../../Input/NumericInput';
import { RetirementGoalOptions } from '../RetirementGoalPage.hooks';

const INITIAL_VALUES: RetirementGoalOptions = {
  targetAsset: 900_000_000,
  currentAsset: 0,
  currentAge: 35,
  retirementAge: 60,
  expectedReturnRate: 7,
};

const schema = yup.object().shape({
  targetAsset: yup
    .number()
    .min(1_000_000, '목표 자금은 100만 원 이상이어야 합니다.')
    .required('필수 입력 항목입니다.'),
  currentAsset: yup.number().min(0, '0 이상의 값을 입력해 주세요.').required('필수 입력 항목입니다.'),
  currentAge: yup
    .number()
    .min(15, '15세 이상을 입력해 주세요.')
    .max(79, '79세 이하를 입력해 주세요.')
    .required('필수 입력 항목입니다.'),
  retirementAge: yup
    .number()
    .min(16, '16세 이상을 입력해 주세요.')
    .max(80, '80세 이하를 입력해 주세요.')
    .moreThan(yup.ref('currentAge'), '은퇴 나이는 현재 나이보다 커야 합니다.')
    .required('필수 입력 항목입니다.'),
  expectedReturnRate: yup
    .number()
    .min(0, '0% 이상을 입력해 주세요.')
    .max(50, '수익률은 50% 이하로 입력해 주세요.')
    .required('필수 입력 항목입니다.'),
});

const FieldTooltip = ({ text }: { text: string }) => (
  <Tooltip title={text} arrow placement="top">
    <IconButton size="small" sx={{ ml: 0.5, p: 0.25, color: '#9ca3af' }}>
      <InfoOutlinedIcon sx={{ fontSize: '1rem' }} />
    </IconButton>
  </Tooltip>
);

export type RetirementGoalFormProps = {
  initialValues?: Partial<RetirementGoalOptions>;
  onSubmit: (options: RetirementGoalOptions) => void;
  className?: string;
};

export function RetirementGoalForm({ initialValues, onSubmit, className }: RetirementGoalFormProps) {
  const handleSubmit = useCallback((opts: RetirementGoalOptions) => onSubmit(opts), [onSubmit]);
  const merged = { ...INITIAL_VALUES, ...initialValues };

  return (
    <Formik
      initialValues={merged}
      validationSchema={schema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ errors, setFieldValue }) => (
        <Form className={classNames(className)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Field name="targetAsset">
              {({ field }: FieldProps) => (
                <NumericInput
                  label={
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      목표 은퇴 자산 (원)
                      <FieldTooltip text="모르겠다면 월 생활비 × 300을 기본값으로 사용 (예: 월 300만원 → 9억원)" />
                    </span>
                  }
                  placeholder="900,000,000"
                  required
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(v) => setFieldValue(field.name, v.floatValue ?? 0)}
                />
              )}
            </Field>
            {errors.targetAsset && (
              <div style={{ color: '#ef4444', fontSize: '0.8125rem' }}>{errors.targetAsset}</div>
            )}

            <Field name="currentAsset">
              {({ field }: FieldProps) => (
                <NumericInput
                  label={
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      현재 보유 자산 (원)
                      <FieldTooltip text="주식, 펀드, 예금 등 금융 자산 합계. 실거주 부동산 제외" />
                    </span>
                  }
                  placeholder="0"
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(v) => setFieldValue(field.name, v.floatValue ?? 0)}
                />
              )}
            </Field>
            {errors.currentAsset && (
              <div style={{ color: '#ef4444', fontSize: '0.8125rem' }}>{errors.currentAsset}</div>
            )}

            <Field name="currentAge">
              {({ field }: FieldProps) => (
                <NumericInput
                  label={
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      현재 나이 (세)
                      <FieldTooltip text="30세 시작 vs 40세 시작 — 필요 월 저축액 절반 이하 차이가 납니다." />
                    </span>
                  }
                  placeholder="35"
                  required
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(v) => setFieldValue(field.name, v.floatValue)}
                />
              )}
            </Field>
            {errors.currentAge && (
              <div style={{ color: '#ef4444', fontSize: '0.8125rem' }}>{errors.currentAge}</div>
            )}

            <Field name="retirementAge">
              {({ field }: FieldProps) => (
                <NumericInput
                  label={
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      목표 은퇴 나이 (세)
                      <FieldTooltip text="5년 늦추면 필요 월 저축액 약 30~40% 감소 효과" />
                    </span>
                  }
                  placeholder="60"
                  required
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(v) => setFieldValue(field.name, v.floatValue)}
                />
              )}
            </Field>
            {errors.retirementAge && (
              <div style={{ color: '#ef4444', fontSize: '0.8125rem' }}>{errors.retirementAge}</div>
            )}

            <Field name="expectedReturnRate">
              {({ field }: FieldProps) => (
                <NumericInput
                  label={
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      예상 연 수익률 (%)
                      <FieldTooltip text="글로벌 분산 ETF 장기 기준 6~8% 합리적 가정. default: 7" />
                    </span>
                  }
                  placeholder="7"
                  required
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(v) => setFieldValue(field.name, v.floatValue)}
                />
              )}
            </Field>
            {errors.expectedReturnRate && (
              <div style={{ color: '#ef4444', fontSize: '0.8125rem' }}>
                {errors.expectedReturnRate}
              </div>
            )}
          </div>
          <div style={{ marginTop: '24px' }}>
            <Button variant="contained" type="submit" fullWidth size="large"
              sx={{ background: '#000033', '&:hover': { background: '#001166' } }}>
              계산하기
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
}
